// BusinessReports.jsx — the business seen from day one, inside Elyon.
//
// Owner directive 2026-09-02: keep the reports in the app. Every revenue view
// in Elyon used to start in June 2026 (when the practice moved onto it), which
// made the business look like it had jumped 7x overnight. This joins the
// AestheticRecord history (revenue_history, Apr 2025 – May 2026) to live
// payments, so the timeline is honest from the first month.
//
// Revenue per visit is the number that explains the story: the calendar and
// the money moved in opposite directions for a year.
import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const C = {
  card: "#ffffff", border: "#e8e4df", borderGold: "rgba(180,140,60,0.3)", goldBg: "rgba(180,140,60,0.08)",
  gold: "#b48c3c", goldD: "#8a6f2e", green: "#2e8b57", greenL: "rgba(46,139,87,0.12)",
  red: "#c0392b", redL: "rgba(192,57,43,0.10)", blue: "#2563eb",
  text: "#1a1a2e", muted: "#6b7280", dim: "#9ca3af", light: "#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const money = (v) => "$" + Math.round(Number(v) || 0).toLocaleString("en-US");
const monthLabel = (m) => {
  const [y, mo] = String(m || "").split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[(+mo || 1) - 1]} ${String(y).slice(2)}`;
};

export default function BusinessReports() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { load(); }, []);

  const load = async () => {
    setErr("");
    try {
      // 1. Pre-Elyon history (loaded from the AestheticRecord export).
      const { data: hist } = await supabase.from("revenue_history")
        .select("month,total_collected,refunds,invoices,source_system").order("month");

      // 2. Live payments, aggregated by month.
      const pays = [];
      for (let from = 0; ; from += 1000) {
        const { data, error } = await supabase.from("payments")
          .select("amount,status,refunded,refund_amount,created_at").order("id").range(from, from + 999);
        if (error) break;
        pays.push(...data);
        if (data.length < 1000) break;
      }
      const live = {};
      for (const p of pays) {
        const m = (p.created_at || "").slice(0, 7);
        if (!m) continue;
        // Count gross cash in and track reversals separately, matching how
        // revenue_history reports the AestheticRecord months. Dropping
        // refunded rows here would report the two eras on different bases and
        // understate the live months against their own history.
        const amount = Number(p.amount) || 0;
        const voided = /void|cancel/i.test(p.status || "");
        const reversed = voided || !!p.refunded || /refund|fail/i.test(p.status || "");
        live[m] = live[m] || { collected: 0, refunds: 0, invoices: 0 };
        live[m].collected += amount;
        live[m].refunds += reversed ? (voided ? amount : (Number(p.refund_amount) || amount)) : 0;
        live[m].invoices++;
      }

      // 3. Visits + provider-days from appointments.
      const appts = [];
      for (let from = 0; ; from += 1000) {
        const { data, error } = await supabase.from("appointments")
          .select("date,status,cid,prov").order("id").range(from, from + 999);
        if (error) break;
        appts.push(...data);
        if (data.length < 1000) break;
      }
      const act = {};
      for (const a of appts) {
        if (!/^\d{4}-\d{2}-\d{2}/.test(a.date || "")) continue;
        const m = a.date.slice(0, 7);
        act[m] = act[m] || { visits: 0, clients: new Set(), provDays: new Set(), cancelled: 0 };
        if (["cancelled", "no-show"].includes(a.status)) { act[m].cancelled++; continue; }
        act[m].visits++;
        if (a.cid) act[m].clients.add(a.cid);
        if (a.prov) act[m].provDays.add(a.prov + "|" + a.date);
      }

      // The history table is authoritative up to the month Elyon took over;
      // live payments are authoritative from there on.
      const HANDOVER = "2026-06";
      const months = [...new Set([
        ...(hist || []).map((h) => h.month),
        ...Object.keys(live),
      ])].filter((m) => m >= "2025-04").sort();

      const today = new Date().toISOString().slice(0, 7);
      const out = months.filter((m) => m <= today).map((m) => {
        const useLive = m >= HANDOVER;
        const h = (hist || []).find((x) => x.month === m);
        const l = live[m];
        const collected = useLive ? (l?.collected || 0) : Number(h?.total_collected || 0);
        const refunds = useLive ? (l?.refunds || 0) : Number(h?.refunds || 0);
        const a = act[m] || { visits: 0, clients: new Set(), provDays: new Set(), cancelled: 0 };
        return {
          month: m,
          collected,
          refunds,
          source: useLive ? "Elyon" : (h?.source_system || "—"),
          visits: a.visits,
          clients: a.clients.size,
          provDays: a.provDays.size,
          cancelled: a.cancelled,
          perVisit: a.visits ? collected / a.visits : 0,
        };
      });
      setRows(out);
    } catch (e) {
      setErr(e.message || String(e));
    }
  };

  if (err) return <div style={{ padding: 20, background: C.redL, color: C.red, borderRadius: 10, fontSize: 13 }}>Couldn't load reports: {err}</div>;
  if (!rows) return <div style={{ padding: 40, textAlign: "center", color: C.muted, fontFamily: F }}>Loading reports…</div>;

  const total = rows.reduce((s, r) => s + r.collected, 0);
  const totalRefunds = rows.reduce((s, r) => s + (r.refunds || 0), 0);
  const last12 = rows.slice(-12);
  const ttm = last12.reduce((s, r) => s + r.collected, 0);
  const peak = rows.reduce((b, r) => (r.collected > b.collected ? r : b), rows[0] || { collected: 0 });
  const maxRev = Math.max(...rows.map((r) => r.collected), 1);
  const maxPer = Math.max(...rows.map((r) => r.perVisit), 1);

  const Tile = ({ label, value, sub, color }) => (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 11, padding: "14px 16px" }}>
      <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.06em" }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: color || C.text, fontFamily: S, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, color: C.muted, marginTop: 3 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div>
        <div style={{ fontSize: 22, fontWeight: 800, color: C.gold, fontFamily: S }}>Business Reports</div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
          Every month since the practice opened. Figures through May 2026 come from AestheticRecord;
          June 2026 onward is live from Elyon.
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(165px,1fr))", gap: 12 }}>
        <Tile label="Collected since opening" value={money(total)}
              sub={`${rows.length} months · ${money(total - totalRefunds)} net of refunds`} color={C.gold} />
        <Tile label="Last 12 months" value={money(ttm)} sub={`avg ${money(ttm / (last12.length || 1))}/mo`} color={C.green} />
        <Tile label="Best month" value={money(peak.collected)} sub={monthLabel(peak.month)} />
        <Tile label="Latest month" value={money(rows[rows.length - 1]?.collected)} sub={monthLabel(rows[rows.length - 1]?.month)} />
      </div>

      {/* Revenue by month */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Collected revenue by month</div>
        <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 14 }}>Cash actually taken in — the figure that ties to the bank</div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 170, overflowX: "auto" }}>
          {rows.map((r) => (
            <div key={r.month} title={`${monthLabel(r.month)} — ${money(r.collected)} (${r.source})`}
                 style={{ flex: "1 0 30px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%" }}>
              <div style={{ fontSize: 9, color: C.muted, marginBottom: 3, whiteSpace: "nowrap" }}>{Math.round(r.collected / 1000)}k</div>
              <div style={{ width: "100%", maxWidth: 34, minHeight: 2, borderRadius: "4px 4px 0 0",
                height: `${(r.collected / maxRev) * 100}%`,
                background: r.source === "Elyon" ? C.gold : "rgba(180,140,60,0.45)" }} />
              <div style={{ fontSize: 9, color: C.muted, marginTop: 5, whiteSpace: "nowrap" }}>{monthLabel(r.month)}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 10.5, color: C.muted, marginTop: 10 }}>
          <span style={{ display: "inline-block", width: 10, height: 10, background: "rgba(180,140,60,0.45)", borderRadius: 2, marginRight: 5 }} />AestheticRecord
          <span style={{ display: "inline-block", width: 10, height: 10, background: C.gold, borderRadius: 2, margin: "0 5px 0 14px" }} />Elyon (live)
        </div>
      </div>

      {/* Revenue per visit — the story */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Revenue per visit</div>
        <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 14 }}>
          What each appointment is worth. When this falls while visits rise, the calendar is filling with cheaper work.
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 130, overflowX: "auto" }}>
          {rows.map((r) => (
            <div key={r.month} title={`${monthLabel(r.month)} — ${money(r.perVisit)}/visit across ${r.visits} visits`}
                 style={{ flex: "1 0 30px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%" }}>
              <div style={{ fontSize: 9, color: C.muted, marginBottom: 3 }}>{Math.round(r.perVisit)}</div>
              <div style={{ width: "100%", maxWidth: 34, minHeight: 2, borderRadius: "4px 4px 0 0",
                height: `${(r.perVisit / maxPer) * 100}%`,
                background: r.perVisit >= 300 ? C.green : r.perVisit >= 200 ? C.gold : C.red }} />
              <div style={{ fontSize: 9, color: C.muted, marginTop: 5, whiteSpace: "nowrap" }}>{monthLabel(r.month)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* The table */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Month by month</div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 640 }}>
            <thead>
              <tr>
                {["Month", "Collected", "Visits", "Clients", "Provider-days", "$/visit", "Cancelled", "Source"].map((h) => (
                  <th key={h} style={{ textAlign: h === "Month" || h === "Source" ? "left" : "right", padding: "9px 14px", fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: `2px solid ${C.border}`, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice().reverse().map((r) => (
                <tr key={r.month}>
                  <td style={{ padding: "9px 14px", borderBottom: `1px solid ${C.border}`, fontWeight: 700, color: C.text, whiteSpace: "nowrap" }}>{monthLabel(r.month)}</td>
                  <td style={{ padding: "9px 14px", borderBottom: `1px solid ${C.border}`, textAlign: "right", fontWeight: 700, color: C.gold, fontVariantNumeric: "tabular-nums" }}>{money(r.collected)}</td>
                  <td style={{ padding: "9px 14px", borderBottom: `1px solid ${C.border}`, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.visits}</td>
                  <td style={{ padding: "9px 14px", borderBottom: `1px solid ${C.border}`, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.clients}</td>
                  <td style={{ padding: "9px 14px", borderBottom: `1px solid ${C.border}`, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.provDays}</td>
                  <td style={{ padding: "9px 14px", borderBottom: `1px solid ${C.border}`, textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums", color: r.perVisit >= 300 ? C.green : r.perVisit >= 200 ? C.text : C.red }}>{money(r.perVisit)}</td>
                  <td style={{ padding: "9px 14px", borderBottom: `1px solid ${C.border}`, textAlign: "right", color: C.muted, fontVariantNumeric: "tabular-nums" }}>{r.cancelled}</td>
                  <td style={{ padding: "9px 14px", borderBottom: `1px solid ${C.border}`, fontSize: 11, color: C.muted, whiteSpace: "nowrap" }}>{r.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
        "Collected" is cash taken that month. Package redemptions are not counted again as new cash —
        they were paid for when the package was bought. Appointment counts come from the calendar and
        are complete for the whole period, including the months that predate Elyon.
      </div>
    </div>
  );
}
